/**
 * @file Frontend utility — a3ManufacturingProcessResolution.
 */

import type { FormDataResponse, FormField } from "../interfaces/formInterfaces";
import type { ManufacturingProcessRow } from "../services/manufacturingProcessesPublicService";

/**
 * True for fuel-section fields that mirror Manufacturing Process Details (process name / id).
 * Rendered as disabled inputs showing the resolved process name, not editable.
 */
export function isA3FuelLockedManufacturingProcessField(
  f: FormField,
  sectionCode: string
): boolean {
  if (sectionCode !== "A3_PROCESS_FUEL_DETAILS") return false;
  if (f.type === "MANUFACTURING_PROCESS") return true;
  const n = (f.name || "").toLowerCase();
  const lab = (f.label || "").toLowerCase();
  /** Never treat measurement fields as the duplicated process selector. */
  if (
    n.includes("fueldensity") ||
    n.includes("fuelconsumptiondensity") ||
    n.includes("processfuelconsumptiondensity") ||
    n.includes("calorific") ||
    (n.includes("fuel") && n.includes("quantity")) ||
    n.includes("fuelmix") ||
    n.includes("fuel_mix")
  ) {
    return false;
  }
  if (f.type === "DROPDOWN" || f.type === "TEXT") {
    if (
      n.includes("a3processfuel") ||
      n.includes("processfuelconsumption") ||
      n.includes("process_fuel")
    ) {
      return true;
    }
    const looksLikeProcessPicker =
      (n.includes("processname") || n.includes("process_name")) &&
      !n.includes("density") &&
      !n.includes("calorific");
    if (looksLikeProcessPicker) return true;
    if (
      (lab.includes("manufacturing process") || lab.includes("process")) &&
      (lab.includes("name") ||
        lab.includes("technology") ||
        lab.includes("selector")) &&
      !lab.includes("density") &&
      !lab.includes("calorific") &&
      !lab.includes("quantity")
    ) {
      return true;
    }
  }
  return false;
}

/**
 * True for embodied-energy section fields that mirror Manufacturing Process Details
 * (process name / id). Shown as read-only resolved process name like fuel rows.
 */
export function isA3EmbodiedEnergyLockedManufacturingProcessField(
  f: FormField,
  sectionCode: string
): boolean {
  if (sectionCode !== "A3_EMBODIED_ENERGY_DETAILS") return false;
  if (f.type === "MANUFACTURING_PROCESS") return true;
  if (f.type === "UNIT") return false;
  const n = (f.name || "").toLowerCase();
  const lab = (f.label || "").toLowerCase();
  /** Numeric embodied energy value — never the duplicated process selector. */
  if (
    n.includes("embodiedenergy") &&
    !n.includes("process") &&
    !n.includes("carbon")
  ) {
    return false;
  }
  if (f.type === "DROPDOWN" || f.type === "TEXT") {
    if (n.includes("embodiedenergy") && n.includes("process")) return true;
    const looksLikeProcessPicker =
      (n.includes("processname") || n.includes("process_name")) &&
      !n.includes("density");
    if (looksLikeProcessPicker) return true;
    if (
      (lab.includes("manufacturing process") || lab.includes("process")) &&
      (lab.includes("name") ||
        lab.includes("technology") ||
        lab.includes("selector")) &&
      !lab.includes("density")
    ) {
      return true;
    }
  }
  return false;
}

/**
 * True for embodied-carbon section fields that mirror Manufacturing Process Details
 * (process name / id). Same UX as embodied energy: read-only resolved process name.
 */
export function isA3EmbodiedCarbonLockedManufacturingProcessField(
  f: FormField,
  sectionCode: string
): boolean {
  if (sectionCode !== "A3_EMBODIED_CARBON_DETAILS") return false;
  if (f.type === "MANUFACTURING_PROCESS") return true;
  if (f.type === "UNIT") return false;
  const n = (f.name || "").toLowerCase();
  const lab = (f.label || "").toLowerCase();
  if (
    n.includes("characterization") ||
    (n.includes("carbon") && !n.includes("process")) ||
    n.includes("functional") ||
    n.includes("reference") ||
    n.includes("rawmaterial")
  ) {
    return false;
  }
  if (f.type === "DROPDOWN" || f.type === "TEXT") {
    if (n.includes("embodiedenergy") && n.includes("process")) return true;
    const looksLikeProcessPicker =
      (n.includes("processname") || n.includes("process_name")) &&
      !n.includes("density");
    if (looksLikeProcessPicker) return true;
    if (
      (lab.includes("manufacturing process") || lab.includes("process")) &&
      (lab.includes("name") ||
        lab.includes("technology") ||
        lab.includes("selector")) &&
      !lab.includes("density")
    ) {
      return true;
    }
  }
  return false;
}

/** CMS field name for the read-only manufacturing process cell in Embodied Carbon (one per sub-process row). */
export function discoverA3EmbodiedCarbonMirroredProcessFieldName(
  formData: FormDataResponse | null
): string | null {
  const sec = formData?.formSubSections?.find(
    (s) => s.code === "A3_EMBODIED_CARBON_DETAILS"
  );
  if (!sec?.fields) return null;
  for (const f of sec.fields) {
    if (
      isA3EmbodiedCarbonLockedManufacturingProcessField(
        f,
        "A3_EMBODIED_CARBON_DETAILS"
      )
    ) {
      return f.name;
    }
  }
  return null;
}

/** DROPDOWN in Process Details that should use GET /manufacturing-processes (CMS label/name varies). */
export function isManufacturingProcessTechnologyField(
  f: FormField,
  sectionCode: string
): boolean {
  if (sectionCode !== "A3_PROCESS_DETAILS" || f.type !== "DROPDOWN") {
    return false;
  }
  const name = (f.name || "").toLowerCase();
  const label = (f.label || "").toLowerCase();
  if (name.includes("processname")) return true;
  if (
    label.includes("manufacturing process") ||
    label.includes("process / technology") ||
    label.includes("process/technology") ||
    (label.includes("process") && label.includes("technology"))
  ) {
    return true;
  }
  return false;
}

function resolveDropdownOrIdToProcessName(
  raw: string,
  field: FormField,
  apiProcesses?: ManufacturingProcessRow[]
): string | null {
  const t = raw.trim();
  if (!t) return null;

  if (apiProcesses?.length) {
    if (apiProcesses.some((p) => p.name === t)) return t;
    const byId = apiProcesses.find(
      (p) => String(p.id) === t || p.id === Number(t)
    );
    if (byId?.name) return String(byId.name).trim();
  }

  const opts = field.dropdown?.options ?? field.dropdownValues ?? [];
  const fromOpts = opts.find(
    (o) => String(o.value) === t || o.label === t
  );
  if (fromOpts?.label) return String(fromOpts.label).trim();

  if (!/^\d+$/.test(t)) return t;
  return null;
}

/** Map stored id string (e.g. "22") to process name using GET /manufacturing-processes list. */
export function resolveManufacturingProcessIdToName(
  raw: string,
  apiProcesses?: ManufacturingProcessRow[]
): string | null {
  const t = raw.trim();
  if (!t || !apiProcesses?.length) return null;
  if (!/^\d+$/.test(t)) return null;
  const id = Number(t);
  if (!Number.isFinite(id)) return null;
  const p =
    apiProcesses.find((x) => x.id === id) ||
    apiProcesses.find((x) => String(x.id) === t);
  return p?.name ? String(p.name).trim() : null;
}

/** Prefer human-readable process name; resolve numeric ids via `apiProcesses` when possible. */
function normalizeStoredProcessToken(
  stored: string,
  apiProcesses?: ManufacturingProcessRow[]
): string {
  const t = stored.trim();
  if (!t) return "";
  const fromId = resolveManufacturingProcessIdToName(t, apiProcesses);
  if (fromId) return fromId;
  return t;
}

/**
 * True when the given Manufacturing Process Details row has a process selected
 * (legacy process name key, TEXT process name, technology DROPDOWN, or MANUFACTURING_PROCESS).
 */
export function hasA3ProcessDetailsProcessSelectionForRow(
  formData: FormDataResponse,
  values: Record<string, string>,
  rowIndex: number
): boolean {
  const sec = formData.formSubSections?.find(
    (s) => s.code === "A3_PROCESS_DETAILS"
  );
  const legacyKey = `processName_A3_01_A3_PROCESS_DETAILS_row${rowIndex}`;
  if (String(values[legacyKey] ?? "").trim() !== "") {
    return true;
  }
  if (!sec) return false;

  for (const f of sec.fields) {
    const key = `${f.name}_${sec.code}_row${rowIndex}`;
    const raw = String(values[key] ?? "").trim();
    if (!raw) continue;
    if (
      f.type === "TEXT" &&
      (f.name || "").toLowerCase().includes("processname")
    ) {
      return true;
    }
    if (
      f.type === "DROPDOWN" &&
      isManufacturingProcessTechnologyField(f, sec.code)
    ) {
      return true;
    }
    if (f.type === "MANUFACTURING_PROCESS") {
      return true;
    }
  }
  return false;
}

/** True when at least one Manufacturing Process Details row has a process selected. */
export function hasAnyA3ProcessDetailsProcessSelection(
  formData: FormDataResponse,
  values: Record<string, string>,
  processRowCount: number
): boolean {
  const n = Math.max(1, processRowCount);
  for (let i = 0; i < n; i++) {
    if (hasA3ProcessDetailsProcessSelectionForRow(formData, values, i)) {
      return true;
    }
  }
  return false;
}

/** A3 API expects `processName` text; map MANUFACTURING_PROCESS / DROPDOWN (id) to a display name. */
export function resolveA3ProcessNameForSave(
  formData: FormDataResponse,
  values: Record<string, string>,
  rowIndex: number,
  apiProcesses?: ManufacturingProcessRow[]
): string {
  const sec = formData.formSubSections?.find(
    (s) => s.code === "A3_PROCESS_DETAILS"
  );
  const legacyKey = `processName_A3_01_A3_PROCESS_DETAILS_row${rowIndex}`;
  const legacy = String(values[legacyKey] ?? "").trim();
  if (legacy) {
    const norm = normalizeStoredProcessToken(legacy, apiProcesses);
    if (!/^\d+$/.test(norm)) return norm;
  }

  if (sec) {
    for (const f of sec.fields) {
      const key = `${f.name}_${sec.code}_row${rowIndex}`;
      if (
        f.type === "TEXT" &&
        (f.name || "").toLowerCase().includes("processname")
      ) {
        const t = String(values[key] ?? "").trim();
        if (t) return normalizeStoredProcessToken(t, apiProcesses);
      }
    }

    for (const f of sec.fields) {
      if (f.type !== "DROPDOWN") continue;
      if (!isManufacturingProcessTechnologyField(f, sec.code)) continue;
      const key = `${f.name}_${sec.code}_row${rowIndex}`;
      const raw = String(values[key] ?? "").trim();
      if (!raw) continue;
      const resolved = resolveDropdownOrIdToProcessName(raw, f, apiProcesses);
      if (resolved) return resolved;
      const fromId = resolveManufacturingProcessIdToName(raw, apiProcesses);
      if (fromId) return fromId;
    }

    for (const f of sec.fields) {
      if (f.type !== "MANUFACTURING_PROCESS") continue;
      const key = `${f.name}_${sec.code}_row${rowIndex}`;
      const raw = String(values[key] ?? "").trim();
      if (!raw) continue;
      const id = Number(raw);
      const list = f.manufacturingProcesses ?? [];
      const mp =
        list.find((p) => p.id === id) ||
        list.find((p) => String(p.id) === raw) ||
        list.find((p) => p.name === raw);
      if (mp?.name) return String(mp.name).trim();
      if (apiProcesses?.length) {
        const fromCat =
          apiProcesses.find((p) => p.id === id) ||
          apiProcesses.find((p) => String(p.id) === raw) ||
          apiProcesses.find((p) => p.name === raw);
        if (fromCat?.name) return String(fromCat.name).trim();
      }
      const fromId = resolveManufacturingProcessIdToName(raw, apiProcesses);
      if (fromId) return fromId;
      return raw;
    }
  }

  if (legacy) {
    return normalizeStoredProcessToken(legacy, apiProcesses);
  }

  return "";
}
